import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, Mail, Lock, User, Eye, EyeOff, Send } from "lucide-react";
import { PhoneInput } from "./ui";
import DemoRequestForm from "./DemoRequest";
import { errorMessage, getCountries, loginEmail, registerEmail, forgotPassword, resetPassword, ssoToken, googleConfig, googleLogin, forcePasswordUpdate, safeRedirect, safeReturn, safeNext, sendBack } from "./api";

// A short, calm loader: shown for a moment before the form first appears, and while switching between
// sign in / register or between the register tabs, so the change doesn't snap.
const FIRST_LOAD_MS = 1000;
const SWITCH_MS = 600;
function Loader({ label }) {
  return (
    <div className="grid place-items-center py-24" role="status" aria-live="polite" data-testid="loader">
      <div className="h-10 w-10 rounded-full border-4 border-sand-300 border-t-forest-600 animate-spin" />
      {label && <p className="mt-4 text-sm text-foreground/60">{label}</p>}
    </div>
  );
}

const inputCls =
  "w-full rounded-xl border border-sand-300 bg-white px-10 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-forest-600/40 focus:border-forest-600";
const phoneCls =
  "w-full rounded-xl border border-sand-300 bg-white px-3 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-forest-600/40 focus:border-forest-600";

/* ===================================================== Forgot password (emailed 6-digit code) */
function ForgotPasswordModal({ initialEmail, onClose, onDone }) {
  const [step, setStep] = useState("request"); // request | verify
  const [email, setEmail] = useState(initialEmail || "");
  const [code, setCode] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [busy, setBusy] = useState(false);

  const request = async (e) => {
    e?.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await forgotPassword(email.trim().toLowerCase());
      toast.success("If an account exists, a 6-digit code is on its way to your email.");
      setStep("verify");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send the code");
    } finally { setBusy(false); }
  };

  const reset = async (e) => {
    e.preventDefault();
    if (code.length !== 6 || newPwd.length < 6) return toast.error("Enter the 6-digit code and a password of 6+ characters");
    setBusy(true);
    try {
      await resetPassword(email.trim().toLowerCase(), code, newPwd);
      toast.success("Password updated. Please sign in with your new password.");
      onDone(email.trim().toLowerCase());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid or expired code");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm grid place-items-center p-4" onClick={onClose} data-testid="forgot-modal">
      <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-3xl border border-sand-300 max-w-md w-full p-6 space-y-4">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-gold-600 font-semibold">Password reset</div>
          <h2 className="font-display font-semibold text-2xl text-forest-700 mt-1">
            {step === "request" ? "Get a reset code" : "Set your new password"}
          </h2>
          <p className="text-sm text-foreground/70 mt-1">
            {step === "request" ? "We'll email you a 6-digit code, valid for 10 minutes." : "Enter the code we sent and pick a new password."}
          </p>
        </div>

        {step === "request" ? (
          <form onSubmit={request} className="space-y-3">
            <div className="relative">
              <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                className={inputCls} data-testid="forgot-email" />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-xl border border-sand-300 text-sm">Cancel</button>
              <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="forgot-send">
                <Send className="h-4 w-4" /> {busy ? "Sending…" : "Send code"}
              </button>
            </div>
          </form>
        ) : (
          <form onSubmit={reset} className="space-y-3">
            <input required maxLength={6} inputMode="numeric" pattern="\d{6}" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="6-digit code"
              className="w-full rounded-xl border border-sand-300 bg-white px-4 py-3 text-center text-2xl tracking-[10px] font-display focus:outline-none focus:ring-2 focus:ring-forest-600/40 focus:border-forest-600"
              data-testid="forgot-code" />
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input type="password" required minLength={6} value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
                placeholder="New password (min 6 chars)" className={inputCls} data-testid="forgot-newpwd" />
            </div>
            <div className="flex justify-between items-center gap-2 pt-1">
              <button type="button" onClick={request} disabled={busy} className="text-xs text-foreground/70 hover:underline" data-testid="forgot-resend">
                Didn&apos;t get it? Resend
              </button>
              <button type="submit" disabled={busy} className="btn-primary text-sm" data-testid="forgot-submit">
                {busy ? "Updating…" : "Update password"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Login() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const redirect = safeRedirect(params.get("redirect"));
  const next = safeNext(params.get("next"));   // a page on this site to open after signing in (the checkout)
  const returnTo = safeReturn(params.get("return"));   // a page on the public website to come back to after signing in (no token)
  const [mode, setMode] = useState(params.get("mode") === "register" ? "register" : "login");
  const [name, setName] = useState("");
  const [accountType, setAccountType] = useState("individual"); // register tab: "individual" | "organisation"
  const [demoSubmitted, setDemoSubmitted] = useState(false);       // the school's demo request went in: now it is just "pick a time"
  const [confirm, setConfirm] = useState("");
  const [countries, setCountries] = useState([]);
  const [callingCode, setCallingCode] = useState("+91"); // India by default; searchable to change
  const [mobile, setMobile] = useState("");
  const [age, setAge] = useState("");   // optional: lets us suggest the right age package (Na'im 3-13, 'Adn · Firdaws 14+)
  const [showPw, setShowPw] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [resetToken, setResetToken] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  useEffect(() => { getCountries().then(setCountries).catch(() => {}); }, []);

  // Single sign-on: if there is already a gateway session (cookie), don't show the
  // form again. With a product waiting (?redirect=) hand a fresh token straight
  // back; opened directly, show "You're signed in" with the list of apps.
  // ?switch=1 forces the form (used after a rejected session / "use another account").
  const silent = params.get("silent") === "1";
  const [checking, setChecking] = useState(!silent && params.get("switch") !== "1");

  // Hold the loader for at least a second before the form first shows, so it doesn't flash in.
  const [firstLoadDone, setFirstLoadDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setFirstLoadDone(true), FIRST_LOAD_MS);
    return () => clearTimeout(t);
  }, []);

  // Show the loader briefly, then apply the change (sign in <-> register, register tabs).
  const [switching, setSwitching] = useState(false);
  const switchTimer = useRef(null);
  useEffect(() => () => clearTimeout(switchTimer.current), []);
  const withLoader = (change) => {
    clearTimeout(switchTimer.current);
    setSwitching(true);
    switchTimer.current = setTimeout(() => { change(); setSwitching(false); }, SWITCH_MS);
  };

  // Silent mode: a product embeds this page in a hidden iframe on load to ask
  // "is there a session?". The answer goes back by postMessage, addressed only
  // to the (allow-listed) product origin that embedded us.
  useEffect(() => {
    if (!silent) return;
    const parent = safeRedirect(params.get("parent"));
    if (!parent) return;
    const reply = (token) => window.parent.postMessage({ type: "hv-sso", token }, parent.origin);
    ssoToken().then(reply).catch(() => reply(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!checking) return;
    ssoToken()
      .then((t) => {
        if (redirect) return sendBack(redirect, t);
        if (returnTo) return window.location.replace(returnTo);
        navigate(next || "/dashboard", { replace: true }); // already signed in, opened directly
      })
      .catch(() => setChecking(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sign in with Google (Google Identity Services). The gateway says whether it is
  // configured; if not, the button simply doesn't appear.
  const [googleClientId, setGoogleClientId] = useState(null);
  const googleBtnRef = useRef(null);
  useEffect(() => {
    if (silent) return;
    googleConfig().then(setGoogleClientId).catch(() => {});
  }, [silent]);
  useEffect(() => {
    if (!googleClientId || checking || !firstLoadDone || switching || resetToken || !googleBtnRef.current) return;
    let cancelled = false;
    const render = () => {
      if (cancelled || !window.google?.accounts?.id || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async ({ credential }) => {
          setBusy(true);
          try {
            const r = await googleLogin(credential);
            toast.success("Signed in with Google");
            finish(r.token);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Google sign-in failed");
          } finally {
            setBusy(false);
          }
        },
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "outline", size: "large", shape: "pill", text: mode === "login" ? "signin_with" : "signup_with", width: 360,
      });
    };
    if (window.google?.accounts?.id) render();
    else {
      let script = document.getElementById("google-gsi");
      if (!script) {
        script = document.createElement("script");
        script.id = "google-gsi";
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener("load", render);
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [googleClientId, checking, resetToken, mode, accountType, switching, firstLoadDone]);

  const finish = (token) => {
    if (redirect) return sendBack(redirect, token);
    if (returnTo) return window.location.replace(returnTo);   // replace, so Back from the website doesn't land on the sign-in page again
    navigate(next || "/dashboard"); // opened directly, no product to return to
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        const r = await loginEmail({ email: email.trim().toLowerCase(), password });
        if (r.password_reset_required) {
          setResetToken(r.reset_token);
          toast.info("Please set a new password to continue.");
          return;
        }
        toast.success("Welcome back");
        finish(r.token);
      } else {
        if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
        if (password !== confirm) { toast.error("Passwords don't match"); return; }
        if (accountType === "individual") {
          if (!callingCode) { toast.error("Choose your country code"); return; }
          if (!/^\d{6,15}$/.test(mobile)) { toast.error("Enter a valid mobile number (digits only)"); return; }
        }
        const r = await registerEmail({
          email: email.trim().toLowerCase(), password, name: name.trim(), account_type: accountType,
          ...(accountType === "individual" ? { country_calling_code: callingCode, mobile_number: mobile, ...(/^\d{1,3}$/.test(age) && Number(age) >= 2 ? { age: Number(age) } : {}) } : {}),
        });
        toast.success("Account created. Bismillah!");
        if (accountType === "individual") finish(r.token); // straight in: the dashboard (or the app they came from)
        else navigate("/school-setup"); // a school continues to its roster form
      }
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    if (newPassword !== confirmNewPassword) { toast.error("Passwords don't match"); return; }
    if (newPassword === password) { toast.error("Choose a password different from your temporary one"); return; }
    setBusy(true);
    try {
      const r = await forcePasswordUpdate(resetToken, newPassword);
      toast.success("Password updated");
      finish(r.token);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't update password");
    } finally {
      setBusy(false);
    }
  };

  const switchMode = (next) => withLoader(() => { setMode(next); setAccountType("individual"); setDemoSubmitted(false); setConfirm(""); });

  if (silent) return null;
  if (checking || !firstLoadDone) {
    return <div className="min-h-screen geo-bg grid place-items-center"><Loader label={checking ? "Signing you in…" : "Loading…"} /></div>;
  }

  return (
    <div className="min-h-screen geo-bg grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col p-12 bg-forest-700 text-sand-100 relative overflow-hidden">
        <div className="flex items-center gap-2.5" data-testid="login-brand">
          <img src={`${process.env.PUBLIC_URL}/Hikma-Verse-Logo.png`} alt="Hikmah Verses" className="h-10 w-10 rounded-xl object-cover" />
          <div>
            <div className="font-display font-semibold leading-tight">HikmahVerses</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-sand-100/60">One account, every product</div>
          </div>
        </div>
        <div className="mt-auto">
          <div className="ayah-arabic text-4xl text-sand-100/95 leading-[1.9]" dir="rtl">
            وَلَقَدْ يَسَّرْنَا الْقُرْآنَ لِلذِّكْرِ فَهَلْ مِن مُّدَّكِرٍ
          </div>
          <p className="mt-5 text-sand-100/70 max-w-md">
            &ldquo;And We have certainly made the Qur&apos;an easy for remembrance, so is there any who will remember?&rdquo; &mdash; Al-Qamar 54:17
          </p>
        </div>
        <div className="absolute -bottom-32 -right-32 h-96 w-96 rounded-full bg-gold-500/10 blur-3xl pointer-events-none" />
      </div>

      {/* Right form */}
      <div className="flex flex-col p-6 md:p-12 justify-center">
        <div className={`${mode === "register" && accountType === "organisation" ? "max-w-2xl" : "max-w-md"} w-full mx-auto`} data-testid="auth-card">
          <div className="lg:hidden mb-8 text-forest-700 font-display font-semibold inline-flex items-center gap-2">
            <img src={`${process.env.PUBLIC_URL}/Hikma-Verse-Logo.png`} alt="Hikmah Verses" className="h-8 w-8 rounded-lg object-cover" />
            HikmahVerses
          </div>

          {switching ? (
            <Loader />
          ) : resetToken ? (
            <div data-testid="force-reset-step">
              <h1 className="font-display font-semibold text-3xl text-forest-700">Set a new password</h1>
              <p className="mt-2 text-foreground/70">You signed in with a temporary password. Choose your own password to continue.</p>
              <form onSubmit={submitReset} className="mt-6 space-y-4">
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input required type="password" minLength={8} autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password (min 8 chars)" className={inputCls} data-testid="new-password-input" />
                </div>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input required type="password" minLength={8} autoComplete="new-password" value={confirmNewPassword} onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Confirm new password" className={inputCls} data-testid="confirm-new-password-input" />
                </div>
                {confirmNewPassword && newPassword !== confirmNewPassword && <p className="text-sm text-red-700" data-testid="new-password-mismatch">Passwords don&apos;t match yet.</p>}
                <button disabled={busy || !newPassword || newPassword !== confirmNewPassword} type="submit" className="w-full btn-primary disabled:opacity-60">
                  {busy ? "Updating…" : "Update password"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            </div>
          ) : (
            <>
              {!demoSubmitted && (<>
                <h1 className="font-display font-semibold text-3xl text-forest-700">
                  {mode === "login" ? "Welcome back" : "Begin your journey"}
                </h1>
                <p className="mt-2 text-foreground/70">
                  {mode === "login" ? "Sign in to continue." : accountType === "organisation" ? "Request a demo for your school." : "Create your free HikmahVerses account."}
                </p>
              </>)}

              {/* HIDDEN for now: school demos are requested and booked on the main site (hikmahverses.com/schools/demo), so
                  the sign-up no longer offers "For Organisation". Un-comment to bring the tabs (and the in-app demo form) back.
              {mode === "register" && !demoSubmitted && (
                <div className="mt-6" data-testid="account-type-tabs">
                  <div className="text-xs font-semibold text-forest-700 mb-2">I am signing up as…</div>
                  <div role="tablist" className="grid grid-cols-2 rounded-full border-2 border-forest-600/15 bg-white p-1">
                    {[["individual", "For Individual"], ["organisation", "For Organisation"]].map(([key, label]) => (
                      <button key={key} type="button" role="tab" aria-selected={accountType === key} data-testid={`account-type-${key}`}
                        onClick={() => { if (key !== accountType) withLoader(() => setAccountType(key)); }}
                        className={`rounded-full py-3 text-sm font-display font-semibold transition ${accountType === key ? "bg-forest-700 text-sand-100 shadow" : "text-forest-700"}`}>{label}</button>
                    ))}
                  </div>
                  <p className="mt-2 text-xs text-foreground/60">
                    {accountType === "organisation"
                      ? "Registering a school, madrasa or institute? Tell us about it and book a demo. Our team will walk you through HikmahVerses and set your school up afterwards."
                      : "Learning on your own, or as a parent with your children. Teaching at a registered school? Choose this too — your principal sets up your school account."}
                  </p>
                </div>
              )}
              */}

              {mode === "register" && accountType === "organisation" ? <DemoRequestForm onSignIn={() => switchMode("login")} onSubmitted={() => setDemoSubmitted(true)} /> : (
              <form onSubmit={submit} className="mt-6 space-y-4" data-testid="password-form">
                {mode === "register" && (
                  <div>
                    <label className="block text-xs font-semibold text-forest-700 mb-1.5">{accountType === "organisation" ? "Your name (contact person)" : "Your name"}</label>
                    <div className="relative">
                      <User className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input data-testid="register-name-input" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Aisha Rahman" className={inputCls} />
                    </div>
                  </div>
                )}
                <div>
                  {mode === "register" && <label className="block text-xs font-semibold text-forest-700 mb-1.5">Email</label>}
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input data-testid="email-input" required type={mode === "login" ? "text" : "email"} autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder={mode === "login" ? "Email or username" : "you@example.com"} className={inputCls} />
                  </div>
                </div>
                {mode === "register" && accountType === "individual" && (
                  <div>
                    <label className="block text-xs font-semibold text-forest-700 mb-1.5">Age of the learner</label>
                    <input data-testid="age-input" inputMode="numeric" value={age} onChange={(e) => setAge(e.target.value.replace(/\D/g, "").slice(0, 3))}
                      placeholder="e.g. 9, 16 or 35" className={inputCls} />
                    <p className="mt-1 text-xs text-foreground/60">So we can show the right plan: Na&apos;im (3–13) or &apos;Adn · Firdaws (14+).</p>
                  </div>
                )}
                {mode === "register" && accountType === "individual" && (
                  <div>
                    <label className="block text-xs font-semibold text-forest-700 mb-1.5">Mobile number (WhatsApp)</label>
                    <PhoneInput countries={countries} code={callingCode} onCode={setCallingCode} number={mobile} onNumber={setMobile} testId="signup" inputClass={phoneCls} />
                    {/* Hidden while the individual WhatsApp welcome is off (WATI_INDIVIDUAL_ENABLED=false); restore when it is turned back on.
                    <p className="mt-1 text-xs text-foreground/60">We&apos;ll send your welcome message here. Type your country code or country name to find it.</p> */}
                  </div>
                )}
                <div>
                  {mode === "register" && <label className="block text-xs font-semibold text-forest-700 mb-1.5">Password</label>}
                  <div className="relative">
                    <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input data-testid="password-input" required type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)}
                      minLength={mode === "register" ? 8 : undefined} autoComplete={mode === "login" ? "current-password" : "new-password"}
                      placeholder={mode === "login" ? "Password" : "Choose a password"} className={`${inputCls} pr-11`} />
                    <button type="button" aria-label={showPw ? "Hide password" : "Show password"} data-testid="toggle-password" onClick={() => setShowPw((v) => !v)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-foreground/60">{showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                  </div>
                  {mode === "register" && <p className="mt-1 text-xs text-foreground/60">At least 8 characters.</p>}
                </div>
                {mode === "register" && (
                  <div>
                    <label className="block text-xs font-semibold text-forest-700 mb-1.5">Confirm password</label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <input data-testid="confirm-password-input" required type={showPw ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)}
                        autoComplete="new-password" placeholder="Type it once more" className={inputCls} />
                    </div>
                  </div>
                )}
                {mode === "login" && (
                  <div className="flex justify-end -mt-1">
                    <button type="button" onClick={() => setForgotOpen(true)} className="text-xs text-forest-700 hover:underline" data-testid="forgot-password-link">
                      Forgot password?
                    </button>
                  </div>
                )}
                <button data-testid="auth-submit-btn" disabled={busy} type="submit" className="w-full btn-primary disabled:opacity-60">
                  {busy ? "Working…" : mode === "login" ? "Sign in" : "Create account"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}

              {googleClientId && (mode === "login" || accountType === "individual") && (
                <div className="mt-6" data-testid="google-signin">
                  <div className="flex items-center gap-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    <span className="h-px flex-1 bg-sand-300" /> or <span className="h-px flex-1 bg-sand-300" />
                  </div>
                  <div ref={googleBtnRef} className="mt-4 flex justify-center" />
                </div>
              )}

              {!demoSubmitted && <p className="mt-6 text-sm text-foreground/70 text-center">
                {mode === "login" ? "New to HikmahVerses?" : "Already have an account?"}{" "}
                <button data-testid="auth-toggle-mode" onClick={() => switchMode(mode === "login" ? "register" : "login")}
                  className="text-forest-700 font-semibold hover:underline">
                  {mode === "login" ? "Create one" : "Sign in"}
                </button>
              </p>}
            </>
          )}
        </div>
      </div>
      {forgotOpen && (
        <ForgotPasswordModal
          initialEmail={email}
          onClose={() => setForgotOpen(false)}
          onDone={(e) => { setEmail(e); setPassword(""); setForgotOpen(false); }}
        />
      )}
    </div>
  );
}
